/**
 * Custom React Hooks
 */
import { useState, useEffect, useCallback } from 'react';

// Generic data fetching hook
export function useFetch(fetchFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn(...args);
      setData(result.data || result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, deps); // eslint-disable-line

  return { data, loading, error, refetch: execute };
}

// Bookmarks using localStorage
export function useBookmarks() {
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('algosolve-bookmarks') || '[]');
    } catch { return []; }
  });

  const toggle = useCallback((id) => {
    setBookmarks(prev => {
      const next = prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id];
      localStorage.setItem('algosolve-bookmarks', JSON.stringify(next));
      return next;
    });
  }, []);

  const isBookmarked = useCallback((id) => bookmarks.includes(id), [bookmarks]);

  return { bookmarks, toggle, isBookmarked };
}

// Recently viewed
export function useRecentlyViewed() {
  const [recent, setRecent] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('algosolve-recent') || '[]');
    } catch { return []; }
  });

  const addRecent = useCallback((item) => {
    setRecent(prev => {
      const filtered = prev.filter(r => r.id !== item.id);
      const next = [item, ...filtered].slice(0, 10);
      localStorage.setItem('algosolve-recent', JSON.stringify(next));
      return next;
    });
  }, []);

  return { recent, addRecent };
}

// Debounce hook
export function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}
